import React, { useEffect, useMemo, useState, lazy } from 'react';
import { CircleAlertIcon, Clock3Icon, ExternalLinkIcon, LocateFixedIcon, MapPinIcon, NavigationIcon, UsersIcon } from 'lucide-react';
import type { Pharmacy } from '../lib/platformData';
type LocationState = 'idle' | 'loading' | 'success' | 'denied' | 'unavailable';
type UserCoordinates = {
  latitude: number;
  longitude: number;
};
type PharmacyLocationPanelProps = {
  pharmacy: Pharmacy;
};
export function PharmacyLocationPanel({
  pharmacy
}: PharmacyLocationPanelProps) {
  const [locationState, setLocationState] = useState<LocationState>('idle');
  const [userCoordinates, setUserCoordinates] = useState<UserCoordinates | null>(null);
  useEffect(() => {
    setLocationState('idle');
    setUserCoordinates(null);
  }, [pharmacy.id]);
  const destination = `${pharmacy.coordinates.latitude},${pharmacy.coordinates.longitude}`;
  const directionsUrl = useMemo(() => buildDirectionsUrl(userCoordinates ? `${userCoordinates.latitude},${userCoordinates.longitude}` : undefined, destination), [destination, userCoordinates]);
  const mapEmbedUrl = useMemo(() => buildMapEmbedUrl(userCoordinates, destination), [destination, userCoordinates]);
  const distance = userCoordinates ? calculateDistanceMiles(userCoordinates.latitude, userCoordinates.longitude, pharmacy.coordinates.latitude, pharmacy.coordinates.longitude) : null;
  const requestLocation = () => {
    if (!navigator.geolocation) {
      setLocationState('unavailable');
      return;
    }
    setLocationState('loading');
    navigator.geolocation.getCurrentPosition(position => {
      setUserCoordinates({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude
      });
      setLocationState('success');
    }, error => {
      setUserCoordinates(null);
      setLocationState(error.code === error.PERMISSION_DENIED ? 'denied' : 'unavailable');
    }, {
      enableHighAccuracy: false,
      timeout: 10000,
      maximumAge: 300000
    });
  };
  const activityTone = getActivityTone(pharmacy.footTraffic.status);
  return <section className="mt-6 overflow-hidden rounded-lg border border-[#D7E2D9] bg-white" aria-labelledby="location-panel-title" data-id="element-151" data-mp="3j719egjqhok8">
      <div className="flex flex-col gap-4 border-b border-[#E5E5E5] p-5 sm:flex-row sm:items-start sm:justify-between sm:p-6" data-id="element-152" data-mp="3e8rg6bhkky19">
        <div data-id="element-153" data-mp="39f4yu60go5z4">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#008F82]" data-id="element-154" data-mp="39feua949z5c7">
            Plan your visit
          </p>
          <h2 id="location-panel-title" className="mt-2 text-2xl font-black tracking-tight text-[#1C1C1C]" data-id="element-155" data-mp="30h30xzc0xtg2">
            Location &amp; local activity
          </h2>
          <p className="mt-2 text-sm leading-6 text-[#666]" data-id="element-156" data-mp="2qhviixqfn8m3">
            {pharmacy.address}, Chicago, IL
          </p>
        </div>
        <a href={directionsUrl} target="_blank" rel="noreferrer" className="inline-flex shrink-0 items-center justify-center gap-2 rounded-sm bg-[#008F82] px-4 py-3 text-sm font-bold text-white hover:bg-[#006F65] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#008F82] focus-visible:ring-offset-2" data-id="element-157" data-mp="2gdx4rhmaqzy4">
          <NavigationIcon className="h-4 w-4" aria-hidden="true" data-id="element-158" data-mp="1xoqjbmu4bu8m" /> Directions
          <ExternalLinkIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-159" data-mp="22mscx1p67byc" />
        </a>
      </div>

      <div className="grid lg:grid-cols-[minmax(0,1.45fr)_minmax(17rem,.85fr)]" data-id="element-160" data-mp="27m4wndkqw2wp">
        <div className="border-b border-[#E5E5E5] lg:border-b-0 lg:border-r" data-id="element-161" data-mp="27kado4t1akv9">
          <div className="relative aspect-[16/10] min-h-[250px] bg-[#EEF2EE]" data-id="element-162" data-mp="2ce66ksufxhjk">
            <iframe title={`Map and directions for ${pharmacy.name}`} src={mapEmbedUrl} className="absolute inset-0 h-full w-full border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-id="element-163" data-mp="1ewo9aubzfc7c" />
            <div className="pointer-events-none absolute left-4 top-4 flex max-w-[calc(100%-2rem)] items-center gap-2 bg-white/95 px-3 py-2 text-xs font-bold text-[#1C1C1C] shadow-sm" data-id="element-164" data-mp="3gneqgm2nqotu">
              <MapPinIcon className="h-4 w-4 shrink-0 text-[#A63A2B]" aria-hidden="true" data-id="element-165" data-mp="3qkay6pkqf6xc" />
              <span className="truncate" data-id="element-166" data-mp="858rlsynp0z7">{pharmacy.name}</span>
            </div>
            {locationState === 'success' && distance !== null && <div className="pointer-events-none absolute bottom-4 left-4 flex max-w-[calc(100%-2rem)] items-center gap-2 bg-[#008F82] px-3 py-2 text-xs font-bold text-white shadow-sm" data-id="element-167" data-mp="i4rb68anmkqq">
                <LocateFixedIcon className="h-4 w-4 shrink-0 text-[#E7B158]" aria-hidden="true" data-id="element-168" data-mp="2jwcr0jp6pnvc" />
                Your location · about {formatDistance(distance)} away
              </div>}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 text-xs text-[#666]" data-id="element-169" data-mp="ufqwgbxqxf9b">
            <span data-id="element-170" data-mp="zdk7bi9zuxfx">
              Map shows the pharmacy pin
              {userCoordinates ? ' and your route' : ''}.
            </span>
            <a href={directionsUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-bold text-[#008F82] underline underline-offset-4 hover:text-[#006F65] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#008F82]" data-id="element-171" data-mp="po8yzxuxlwt3">
              Open accessible map{' '}
              <ExternalLinkIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-172" data-mp="1sxu5p4pk7x7" />
            </a>
          </div>
        </div>

        <div className="space-y-5 p-5 sm:p-6" data-id="element-173" data-mp="1w4ug52kdslkv">
          <div data-id="element-174" data-mp="1w547ath5wys5">
            <div className="flex items-start gap-3" data-id="element-175" data-mp="1w2hzqtu9uj9x">
              <span className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${activityTone.dot}`} aria-hidden="true" data-id="element-176" data-mp="213xlyp86qvtw" />
              <div data-id="element-177" data-mp="1zk5r6nbdzdhx">
                <p className="text-xs font-bold uppercase tracking-[0.13em] text-[#B46423]" data-id="element-178" data-mp="1unp3tvc0h35w">
                  Local activity signal
                </p>
                <h3 className="mt-1 text-lg font-black text-[#1C1C1C]" data-id="element-179" data-mp="1pom851hx89al">
                  {pharmacy.footTraffic.status}
                </h3>
              </div>
            </div>
            <p className="mt-3 text-sm leading-6 text-[#555]" data-id="element-180" data-mp="2iboqok67c9f7">
              {pharmacy.footTraffic.summary}
            </p>
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-xs font-semibold text-[#666]" data-id="element-181" data-mp="2deotyzv8sj0r">
              <span className="inline-flex items-center gap-1.5" data-id="element-182" data-mp="28ldj8nnb7i3k">
                <UsersIcon className="h-3.5 w-3.5 text-[#008F82]" aria-hidden="true" data-id="element-183" data-mp="24kk0s9smyvi2" />
                {pharmacy.footTraffic.recentReports} recent check-ins
              </span>
              <span className="inline-flex items-center gap-1.5" data-id="element-184" data-mp="2wbhqd4zphzzb">
                <Clock3Icon className="h-3.5 w-3.5 text-[#B46423]" aria-hidden="true" data-id="element-185" data-mp="iv6halch3caw" />
                Typical wait {pharmacy.footTraffic.typicalWaitMinutes} min
              </span>
            </div>
            <p className="mt-3 border-l-2 border-[#E7B158] pl-3 text-xs leading-5 text-[#777]" data-id="element-186" data-mp="rx5zvi34yofy">
              Community-reported mock signal for this prototype — not verified
              real-time store telemetry. Confirm conditions with the pharmacy
              before you go.
            </p>
          </div>

          <div className="border-t border-[#E5E5E5] pt-5" data-id="element-187" data-mp="1bj2dloy4r275">
            <p className="text-sm font-black text-[#1C1C1C]" data-id="element-188" data-mp="1bjlur3fyhmk4">
              See your distance
            </p>
            <p className="mt-1 text-xs leading-5 text-[#666]" data-id="element-189" data-mp="1fmi5y9362ge1">
              Share your approximate location only when you choose. It stays in
              this browser session.
            </p>
            <button type="button" onClick={requestLocation} disabled={locationState === 'loading'} className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-sm border border-[#B9CDC0] bg-[#F4F8F5] px-4 py-3 text-sm font-bold text-[#008F82] hover:bg-[#EAF3EC] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#008F82] disabled:cursor-wait disabled:opacity-70" data-id="element-190" data-mp="1pgh6j3nlds3v">
              <LocateFixedIcon className={`h-4 w-4 ${locationState === 'loading' ? 'animate-pulse' : ''}`} aria-hidden="true" data-id="element-191" data-mp="hpzr9kd662os" />
              {locationState === 'loading' ? 'Finding your location…' : locationState === 'success' ? 'Update my location' : 'Use my location'}
            </button>
            <LocationMessage state={locationState} distance={distance} data-id="element-192" data-mp="3u50of8dmca6w" />
          </div>
        </div>
      </div>
    </section>;
}
function LocationMessage({
  state,
  distance
}: {
  state: LocationState;
  distance: number | null;
}) {
  if (state === 'idle') return null;
  const isError = state === 'denied' || state === 'unavailable';
  const message = state === 'loading' ? 'Requesting your browser location…' : state === 'success' && distance !== null ? `Your approximate distance is ${formatDistance(distance)}. The map now includes your route.` : state === 'denied' ? 'Location permission was not granted. You can still open directions in Google Maps.' : 'Your browser could not get a location. You can still open directions in Google Maps.';
  return <p className={`mt-3 flex gap-2 text-xs leading-5 ${isError ? 'text-[#A63A2B]' : 'text-[#008F82]'}`} role="status" aria-live="polite" data-id="element-193" data-mp="3oslnk8ooyxwt">
      {isError && <CircleAlertIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-194" data-mp="2dd6pt2ons20n" />}
      {message}
    </p>;
}
function buildDirectionsUrl(origin: string | undefined, destination: string) {
  const parameters = new URLSearchParams({
    api: '1',
    destination,
    travelmode: 'driving'
  });
  if (origin) parameters.set('origin', origin);
  return `https://www.google.com/maps/dir/?${parameters.toString()}`;
}
function buildMapEmbedUrl(userCoordinates: UserCoordinates | null, destination: string) {
  const parameters = new URLSearchParams({
    output: 'embed',
    z: '15'
  });
  if (userCoordinates) {
    parameters.set('saddr', `${userCoordinates.latitude},${userCoordinates.longitude}`);
    parameters.set('daddr', destination);
  } else {
    parameters.set('q', destination);
  }
  return `https://www.google.com/maps?${parameters.toString()}`;
}
function calculateDistanceMiles(startLatitude: number, startLongitude: number, endLatitude: number, endLongitude: number) {
  const earthRadiusMiles = 3958.8;
  const latitudeDifference = toRadians(endLatitude - startLatitude);
  const longitudeDifference = toRadians(endLongitude - startLongitude);
  const a = Math.sin(latitudeDifference / 2) ** 2 + Math.cos(toRadians(startLatitude)) * Math.cos(toRadians(endLatitude)) * Math.sin(longitudeDifference / 2) ** 2;
  return earthRadiusMiles * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
function toRadians(value: number) {
  return value * Math.PI / 180;
}
function formatDistance(distance: number) {
  return distance < 0.2 ? '< 0.2 mi' : `${distance.toFixed(1)} mi`;
}
function getActivityTone(status: Pharmacy['footTraffic']['status']) {
  if (status === 'Busy') return {
    dot: 'bg-[#A63A2B]'
  };
  if (status === 'Moderate') return {
    dot: 'bg-[#E7B158]'
  };
  return {
    dot: 'bg-[#008F82]'
  };
}